import { useState, useCallback } from 'react';
import { validateField, validateForm , businessValidations } from '../constants/validations';
import { formatDateToDDMMYYYY } from '../utils/dateUtils';

interface UseValidationReturn {
  errors: Record<string, string>;
  validateSingleField: (fieldKey: string, value: unknown) => string | null;
  validateAllFields: (data: Record<string, unknown>) => Record<string, string>;
  clearErrors: () => void;
  clearFieldError: (fieldKey: string) => void;
  hasErrors: boolean;
}

export const useValidation = (): UseValidationReturn => {
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateSingleField = useCallback((fieldKey: string, value: unknown): string | null => {
    const error = validateField(fieldKey, value);
    
    setErrors(prev => {
      if (error) {
        return { ...prev, [fieldKey]: error };
      } else {
        const newErrors = { ...prev };
        delete newErrors[fieldKey];
        return newErrors;
      }
    });
    
    return error;
  }, []);

  const validateAllFields = useCallback((data: Record<string, unknown>): Record<string, string> => {
    const validationErrors = validateForm(data);
    setErrors(validationErrors);
    return validationErrors;
  }, []);

  const clearErrors = useCallback(() => {
    setErrors({});
  }, []);

  const clearFieldError = useCallback((fieldKey: string) => {
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[fieldKey];
      return newErrors;
    });
  }, []);

  return {
    errors,
    validateSingleField,
    validateAllFields,
    clearErrors,
    clearFieldError,
    hasErrors: Object.keys(errors).length > 0
  };
};

// Specialized validation hook for payoff estimator
export const usePayoffValidation = () => {
  const { errors, validateSingleField, clearErrors, hasErrors } = useValidation();

  const validatePayoffForm = useCallback((payoffDate: string, loan?: { currentSystemDate?: string; maxPayoffDate?: string }) => {
    const errors: Record<string, string> = {};

    // Validate payoff date
    const dateError = validateSingleField('payoffDate', payoffDate);
    if (dateError) errors.transactionDate = dateError;

    // Business rule validations
    if (payoffDate) {
      const minDate = loan?.currentSystemDate
        ? new Date(loan.currentSystemDate)
        : (() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; })();
      const payoff = new Date(payoffDate);
      if (payoff < minDate) {
        const minDateValue = loan?.currentSystemDate || minDate.toISOString().split('T')[0];
        errors.transactionDate = `Please enter a date on or after ${formatDateToDDMMYYYY(minDateValue) || minDateValue}`;
      }

      if (loan?.maxPayoffDate) {
        const maxDate = new Date(loan.maxPayoffDate);
        if (payoff > maxDate) {
          errors.transactionDate = `Please enter a date less than ${formatDateToDDMMYYYY(loan.maxPayoffDate) || loan.maxPayoffDate}`;
        }
      }
    }

    return errors;
  }, [validateSingleField]);

  return {
    errors,
    validatePayoffForm,
    clearErrors,
    hasErrors
  };
};

// Specialized validation hook for transaction summary
export const useTransactionSummaryValidation = () => {
  const { errors, validateSingleField, clearErrors, hasErrors } = useValidation();
  const { fromDateAfterToDate, toDateBeforeFromDate, toDateEqualsAsOfDate, periodExceedsLimit } = businessValidations;

  /**
   * Validates the transaction summary form fields and business rules.
   * @param fromDate string
   * @param toDate string
   * @param asOfDate string
   * @param checkPeriodLimit boolean (if true, checks for >1 year period)
   */
  const validateTransactionSummaryForm = (
    fromDate: string,
    toDate: string,
    asOfDate: string,
    checkPeriodLimit = false
  ) => {
    const errors: Record<string, string> = {};

    // Field-level validation
    const fromDateError = validateSingleField('fromDate', fromDate);
    if (fromDateError) errors.fromDate = fromDateError;
    const toDateError = validateSingleField('toDate', toDate);
    if (toDateError) errors.toDate = toDateError;
    const asOfDateError = validateSingleField('asOfDate', asOfDate);
    if (asOfDateError) errors.asOfDate = asOfDateError;

    // Business rule validations
    if (!errors.fromDate && !errors.toDate) {
      const fromAfterTo = fromDateAfterToDate(fromDate, toDate);
      if (fromAfterTo) errors.fromDate = fromAfterTo;
      const toBeforeFrom = toDateBeforeFromDate(toDate, fromDate);
      if (toBeforeFrom) errors.toDate = toBeforeFrom;
    }
    if (!errors.toDate && !errors.asOfDate) {
      const toEqualsAsOf = toDateEqualsAsOfDate(toDate, asOfDate);
      if (toEqualsAsOf) errors.toDate = toEqualsAsOf;
    }
    if (checkPeriodLimit && !errors.fromDate && !errors.toDate) {
      const periodLimit = periodExceedsLimit(fromDate, toDate);
      if (periodLimit) errors.period = periodLimit;
    }

    return errors;
  };

  return {
    errors,
    validateTransactionSummaryForm,
    clearErrors,
    hasErrors
  };
}; 


export const usePaymentsValidation = () => {
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  /**
   * Validates Payments tab fields and business rules.
   * @param paymentAmount  string or number (user input)
   * @param outstandingAmount number (from API/data)
   * @param loanStatus string (from loan object, e.g. 'Active')
   * @param backendStatus string (optional, from API error for Invalid_State)
   */
  const validatePaymentsForm = (
    paymentAmount: string | number,
    outstandingAmount: number,
    loanStatus?: string,
    backendStatus?: string
  ) => {
    const errs: Record<string, string> = {};

    // Frontend Field-level checks
    const required = businessValidations.requiredAmountCheck(paymentAmount);
    if (required) errs.paymentAmount = required;

    const isZero = businessValidations.zeroTxnAmountCheck(Number(paymentAmount));
    if (!errs.paymentAmount && isZero) errs.paymentAmount = isZero;

    const tooMuch = businessValidations.transactionAmountValidation(Number(paymentAmount), outstandingAmount);
    if (!errs.paymentAmount && tooMuch) errs.paymentAmount = tooMuch;

    // Backend status checks, shown as toast/snackbar at submit (do NOT block submit button)
    const loanStatusErr = businessValidations.loanStatusCheck(loanStatus);
    if (loanStatusErr) errs.loanStatus = loanStatusErr;

    const invalidStateErr = businessValidations.invalidStateCheck(backendStatus);
    if (invalidStateErr) errs.backendState = invalidStateErr;

    setErrors(errs);
    return errs;
  };

  const clearErrors = useCallback(() => setErrors({}), []);

  return {
    errors,                // Use this to show field errors (inline and/or for toasts)
    validatePaymentsForm,  // Call this on submit, pass all required values
    clearErrors,
    hasErrors: Object.keys(errors).length > 0,
  };
}; 
